/**
 * Firebase bootstrap for Chore Champion.
 *
 * The compat build is used on purpose. This app still runs on Vue CLI 3 with
 * webpack 4, whose parser cannot read the ES2020 syntax in the modular
 * Firebase builds. The compat build ships the same SDK with older syntax.
 */
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import "firebase/compat/firestore";

import config from "./firebase.config";

if (!firebase.apps.length) {
  firebase.initializeApp(config);
}

export const auth = firebase.auth();
export const firestore = firebase.firestore();

// Firestore rejects undefined field values. Skipping them keeps a field that
// has no value out of the write instead of failing the whole write.
firestore.settings({ ignoreUndefinedProperties: true });

/**
 * Sentinel values such as serverTimestamp live on the firestore namespace in
 * the compat build, not on the firestore instance.
 */
export const FieldValue = firebase.firestore.FieldValue;

/**
 * Point the app at the local emulator suite.
 *
 * Set VUE_APP_USE_EMULATORS=true and run `firebase emulators:start` to work
 * against local Auth and Firestore instead of the live project.
 */
if (process.env.VUE_APP_USE_EMULATORS === "true") {
  const authHost = process.env.VUE_APP_AUTH_EMULATOR_HOST || "http://127.0.0.1:9199";
  const firestoreHost = process.env.VUE_APP_FIRESTORE_EMULATOR_HOST || "127.0.0.1";
  const firestorePort = Number(process.env.VUE_APP_FIRESTORE_EMULATOR_PORT || 8380);
  auth.useEmulator(authHost);
  firestore.useEmulator(firestoreHost, firestorePort);
  console.info(`Using the local Firebase emulators at ${authHost} and ${firestoreHost}:${firestorePort}`);
}

/**
 * Resolves once Firebase has restored (or ruled out) a persisted session.
 *
 * `auth.currentUser` is null during startup even when a session exists, so
 * anything that depends on the signed in user has to wait for this promise.
 */
export const authReady = new Promise((resolve) => {
  const unsubscribe = auth.onAuthStateChanged((user) => {
    unsubscribe();
    resolve(user);
  });
});

export default firebase;
